<!-- Binds a (non-reactive) MediaStream to a <video> element. The parent passes a
     fresh stream object whenever meeting.state.streamTick changes. Remote
     streams are fed track by track (see meeting.js), so playback is retried
     whenever a track lands. -->
<template>
  <video ref="video" class="meeting-video"
         :class="['meeting-video--' + fit, { 'meeting-video--mirror': mirror }]"
         autoplay playsinline :muted="muted"></video>
</template>

<script>
export default {
  props: {
    stream: { type: Object, default: null },
    muted: { type: Boolean, default: false },
    mirror: { type: Boolean, default: false },
    fit: { type: String, default: 'cover' }, // cover (camera) | contain (shared screen)
    tick: { type: Number, default: 0 }, // bumped by the parent when tracks land on the same stream
    sinkId: { type: String, default: '' } // audiooutput deviceId chosen in the device check ('' = default)
  },
  watch: {
    stream () { this.bind() },
    tick () { this.play() },
    sinkId () { this.applySink() }
  },
  created () {
    this.bound = null // non-reactive: the stream currently attached
    this.onAdd = null
  },
  mounted () { this.bind() },
  beforeDestroy () { this.detach() },
  methods: {
    bind () {
      const el = this.$refs.video
      if (!el) return
      const next = this.stream || null
      if (this.bound !== next) {
        this.detach()
        this.bound = next
        el.srcObject = next
        if (next) {
          this.onAdd = () => this.play()
          next.addEventListener('addtrack', this.onAdd)
        }
      }
      this.applySink()
      this.play()
    },
    applySink () {
      const el = this.$refs.video
      if (!el || typeof el.setSinkId !== 'function') return
      const want = this.sinkId || ''
      if ((el.sinkId || '') === want) return
      const p = el.setSinkId(want)
      if (p && p.catch) p.catch(() => { /* unplugged speaker: browser default */ })
    },
    detach () {
      if (this.bound && this.onAdd) this.bound.removeEventListener('addtrack', this.onAdd)
      this.onAdd = null
    },
    play () {
      const el = this.$refs.video
      if (!el || !el.srcObject || !el.paused) return
      const p = el.play()
      if (p && p.catch) p.catch(() => { /* autoplay policy: retried on next track / tick */ })
    }
  }
}
</script>

<style scoped>
.meeting-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #141428;
  display: block;
}
.meeting-video--contain { object-fit: contain; background: #000; }
.meeting-video--mirror { transform: scaleX(-1); }
</style>
